import {
  DepartmentsEnum,
  IntegerFilterInput,
  SortOrder,
  SponsoredProductInput,
} from '@moonpig/web-explore-types-graphql'

import { RouterType } from '@moonpig/web-core-routing'
import { removePreAppliedFilters } from './removePreAppliedFilters'
import { SearchStoreState } from '../types'
import {
  ApplicableFilter,
  FilterItem,
  LoadFiltersResponse,
  SearchFilter,
  SearchRequestResponse,
  SearchService,
  SelectedSuggestion,
} from '../../services/types/services'
import { PageType } from '../../types'
import { extractIntegerFilters } from './extractIntegerFilters'

type BuildInitialStateArgs = {
  pageTitle: string
  pageType: PageType
  router: RouterType
  department: DepartmentsEnum[]
  initialResponse: SearchRequestResponse
  redirectedDepartment?: DepartmentsEnum
  redirectionSource?: 'smart_redirection' | 'no_results'
  searchTerm: string
  correctedSearchTerm?: string
  selectedSuggestion?: SelectedSuggestion
  isTrendingSuggestion?: boolean
  promotionId?: string
  initialFiltersResponse: LoadFiltersResponse
  initialFilters: SearchFilter[]
  persistedFilters: string[]
  preAppliedFilters: {
    key: string
    group: string
  }[]
  selectedFilters: ApplicableFilter[]
  hasNbaFilters?: boolean
  hasRudeFilterEnabled?: boolean
  filtersQuery: ({
    filters,
    integerFilter,
  }: {
    filters: SearchFilter[]
    integerFilter?: IntegerFilterInput
  }) => Promise<LoadFiltersResponse>
  services: SearchService
  allFilters: FilterItem[]
  sponsoredProducts?: SponsoredProductInput[]
  offset: number
}

export const buildInitialState = ({
  pageTitle,
  pageType,
  router,
  department,
  initialResponse,
  redirectedDepartment,
  redirectionSource,
  searchTerm,
  correctedSearchTerm,
  selectedSuggestion,
  isTrendingSuggestion,
  promotionId,
  preAppliedFilters,
  selectedFilters,
  hasNbaFilters,
  hasRudeFilterEnabled,
  filtersQuery,
  services,
  allFilters,
  sponsoredProducts,
  offset,
}: BuildInitialStateArgs): Partial<SearchStoreState> => {
  return {
    pageContext: {
      title: pageTitle,
      type: pageType,
      router,
    },
    searchContext: {
      departments: department,
      departmentCounts: initialResponse.departments,
      redirectedDepartment,
      redirectionSource,
      searchTerm,
      correctedSearchTerm,
      selectedSuggestion,
      isTrendingSuggestion,
      promotionId,
    },
    filters: allFilters,
    integerFilters: extractIntegerFilters(allFilters),
    preAppliedFilters,
    selectedFilters,
    queryFilters: async ({
      filters,
      integerFilter,
    }: {
      filters: SearchFilter[]
      integerFilter?: IntegerFilterInput
    }) => {
      const result = await filtersQuery({ filters, integerFilter })
      return removePreAppliedFilters(result.filters, preAppliedFilters)
    },
    sortValue: SortOrder.POPULARITY,
    initialSortValue: SortOrder.POPULARITY,
    results: initialResponse,
    hasNbaFilters,
    hasRudeProducts: hasRudeFilterEnabled,
    initialOffset: offset,
    querySearch: async ({
      newPage,
      filters,
      integerFilter = {},
      sortOrder,
    }: {
      newPage: number
      filters: SearchFilter[]
      integerFilter?: IntegerFilterInput
      sortOrder: SortOrder | undefined
    }) => {
      const searchResponse = await services.search({
        searchTerm,
        departments: department,
        sortOrder,
        filters,
        integerFilter,
        page: newPage,
        promotionId,
        sponsoredProducts: sponsoredProducts || /* istanbul ignore next */ [],
      })
      return searchResponse
    },
    topPage: initialResponse.page,
    bottomPage: initialResponse.page,
    lastLoadedPage: initialResponse.page,
    filtersMenuOpen: false,
    loading: false,
  }
}
